/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { formatDuration } from '@/ux-common/src/lib/utils';
import {
  ChatMessage,
  Conversation,
  KahuApiTurnStatus,
  SubmitTurnFeedbackParams,
  SubmitTurnFeedbackResponse,
} from 'types/kahu';

import { request } from './request';

const API_TURN_STATUSES: readonly KahuApiTurnStatus[] = [
  'completed',
  'cancelled',
  'incomplete',
];

/**
 * The API reports a status on every assistant turn. Older sessions (and any unexpected value) fall back to `completed` so history still renders.
 */
const normalizeTurnStatus = (status: unknown): KahuApiTurnStatus =>
  API_TURN_STATUSES.includes(status as KahuApiTurnStatus)
    ? (status as KahuApiTurnStatus)
    : 'completed';

// get all conversations, sorted by updatedAt descending order.
export const getAllConversations = async (): Promise<Conversation[]> => {
  try {
    const [data] = await request.get(
      { endpoint: 'sessions' },
      false,
      false,
      false,
      false,
      true // sre agent
    );

    return data.sessions.map((session: any) => ({
      id: session.session_id,
      sessionId: session.session_id,
      title: session.preview ?? '',
      description: session.last_agent_message ?? '',
      messages: [],
      createdAt: Date.now(),
      updatedAt: new Date(session.last_update_time).getTime(),
      unreadCount: 0,
    }));
  } catch (e) {
    console.error('Failed to fetch sessions:', e);
    return [];
  }
};

// Fetch a single session's message history from SRE Agent API
export const getSessionById = async (
  sessionId: string
): Promise<ChatMessage[]> => {
  try {
    const [data] = await request.get(
      { endpoint: `sessions/${sessionId}` },
      false,
      false,
      false,
      false,
      true // sreAgent
    );

    return (data.messages ?? []).map((msg: any) => {
      const isUser = msg.role === 'user';

      // The API sends `timestamp` as an ISO string, but the UI (date-fns
      // format) expects epoch ms. Convert and guard against invalid dates.
      const parsedTimestamp = msg.timestamp
        ? new Date(msg.timestamp).getTime()
        : NaN;
      const timestamp = Number.isNaN(parsedTimestamp)
        ? undefined
        : parsedTimestamp;
      return {
        id: crypto.randomUUID(),
        role: isUser ? 'user' : 'assistant',
        response: msg.content ?? '',
        timestamp,
        ...(isUser
          ? {}
          : {
              status: normalizeTurnStatus(msg.status),
              duration:
                formatDuration(msg.duration ?? msg.duration_ms) ?? undefined,
              tokenCount: msg.token_count ?? undefined,
              turnId: msg.turn_id ?? undefined,
            }),
      };
    });
  } catch (e) {
    console.error('Failed to fetch session by id:', e);
    return [];
  }
};

/**
 * Cancels the in-flight turn for a Kahu SRE-agent session
 */
export const cancelSessionTurn = async (
  sessionId: string
): Promise<{ status: 'stop_requested'; session_id: string } | undefined> => {
  try {
    const [data] = await request.post(
      { endpoint: `sessions/${sessionId}/stop` },
      false,
      false,
      false,
      true // sreAgent
    );

    return data;
  } catch (e) {
    console.error('Failed to cancel session turn:', e);
    return undefined;
  }
};

export const submitTurnFeedback = async ({
  sessionId,
  turnId,
  rating,
  comment,
}: SubmitTurnFeedbackParams): Promise<SubmitTurnFeedbackResponse> => {
  const body = {
    turn_id: turnId,
    rating,
    ...(comment?.trim() && { comment: comment.trim() }),
  };

  try {
    const [data] = await request.post(
      {
        endpoint: `sessions/${sessionId}/feedback`,
      },
      body,
      false,
      false,
      true // sreAgent
    );

    return data;
  } catch (error) {
    console.error('Failed to submit turn feedback:', error);
    throw error;
  }
};
